import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Upload, Select, Button, Collapse } from 'antd';
import { UploadOutlined, SettingOutlined } from '@ant-design/icons';
import { Task } from './types';

const { Option } = Select;
const { Panel } = Collapse;

interface TaskModalProps {
  open: boolean;
  loading: boolean;
  form: any; // 接收外部传递的form实例
  onSubmit: () => void;
  onCancel: () => void;
  isEditMode?: boolean; // 是否为编辑模式
  editTask?: Task; // 编辑的任务数据
}

const TaskModal: React.FC<TaskModalProps> = ({
  open,
  loading,
  form,
  onSubmit,
  onCancel,
  isEditMode = false,
  editTask
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  // 当编辑模式打开时，预填表单数据
  useEffect(() => {
    if (isEditMode && editTask && open) {
      form.setFieldsValue({
        task_type: editTask.task_type,
        target_language: editTask.target_language,
        translate_columns: editTask.translate_columns?.join(', '),
        custom_columns: editTask.custom_columns
      });
      // 如果有自定义列名映射，展开高级配置
      if (editTask.custom_columns || editTask.translate_columns?.length > 0) {
        setShowAdvanced(true);
      }
    }
  }, [isEditMode, editTask, open, form]);

  const handleOk = async () => {
    try {
      await form.validateFields();
      onSubmit();
    } catch (error) {
      console.log('表单校验失败:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setShowAdvanced(false);
    onCancel();
  };

  return (
    <Modal
      title={isEditMode ? "编辑重试任务" : "新建文本智能翻译任务"}
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={loading}
      destroyOnHidden
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ task_type: 'language_detection', target_language: '中文' }}
      >
        {!isEditMode && (
          <Form.Item
            name="file"
            label={<span>上传文件 <span style={{ color: '#999', fontSize: 12, marginLeft: 4 }}>支持Excel或CSV文件格式</span></span>}
            valuePropName="fileList"
            getValueFromEvent={e => (Array.isArray(e) ? e : e && e.fileList)}
            rules={[{ required: true, message: '请上传CSV或Excel文件' }]}
          >
            <Upload beforeUpload={() => false} maxCount={1} accept=".csv,.xls,.xlsx">
              <Button icon={<UploadOutlined />}>选择文件</Button>
            </Upload>
          </Form.Item>
        )}
        
        <Form.Item
          name="task_type"
          label={
            <span>
              任务类型
              <span style={{ color: '#ff4d4f', fontSize: 12, marginLeft: 8 }}>
                翻译比语种识别耗时增加约4倍，费用增加约2倍，如非必要请勿选择翻译任务
              </span>
            </span>
          }
          rules={[{ required: true, message: '请选择任务类型' }]}
        >
          <Select placeholder="请选择任务类型">
            <Option value="language_detection">语种识别任务</Option>
            <Option value="translation">翻译任务</Option>
          </Select>
        </Form.Item>
        
        <Form.Item
          name="target_language"
          label="目标语言"
          rules={[{ required: true, message: '请输入目标语言' }]}
        >
          <Input placeholder="例如：中文、英文、日文等" />
        </Form.Item>
        
        <Collapse 
          ghost 
          activeKey={showAdvanced ? ['advanced'] : []}
          onChange={(keys) => setShowAdvanced(keys.includes('advanced'))}
        >
          <Panel 
            header={
              <span>
                <SettingOutlined style={{ marginRight: 8 }} />
                高级配置
              </span>
            } 
            key="advanced"
          >
            <Form.Item
              name="translate_columns"
              label="文本列名"
            >
              <Input placeholder="用逗号分隔，例如：标题,内容,摘要。为空则使用默认列：标题,文本,摘要,内容" />
            </Form.Item>
            
            <Form.Item
              name="custom_columns"
              label={
                <span>
                  自定义列名映射
                  <span style={{ color: '#999', fontSize: 12, marginLeft: 4 }}>
                    可选，用于将文件中的列名映射到系统期望的列名
                  </span>
                </span>
              }
            >
              <Input.TextArea 
                rows={3} 
                placeholder='JSON格式，例如：{"title": "标题", "content": "内容", "summary": "摘要"}'
              />
            </Form.Item>
          </Panel>
        </Collapse>
      </Form>
    </Modal>
  );
};

export default TaskModal;
